import type { Result, SearchParamsFullText, FacetResult } from '@orama/orama';
import type { SearchableDocument, OramaDBType } from './orama-schema';
import type { RxCollectionName, RxJSON } from '@flintmint/core-shared/rxdb';

export type Highlight = {
  name?: string;
  content?: string;
};

export interface SearchResult extends Result<SearchableDocument> {
  highlight?: Highlight;
}

export type OramaSearchParams = SearchParamsFullText<OramaDBType>;
export type SearchOptions = {
  highlightMatches?: boolean;
  includeFacets?: boolean;
};
export type SearchPayload = OramaSearchParams & SearchOptions;

export type SearchResponse = {
  hits: SearchResult[];
  facets?: FacetResult;
  count: number;
};

// Batch insert/update payload item: carries the RAW rxdb doc plus the tag names
// resolved on the main thread. The worker calls buildSearchableDocument on these,
// off-loading the HTML strip / shaping cost from the main thread.
export type RawSearchDoc = {
  collectionName: RxCollectionName;
  doc: RxJSON;
  tagNames: string[];
};

export type WorkerRequest = {
  action: string;
  messageId: string;
  id?: string;
  // Mixed contract by action:
  // - 'init': { indexKey, validIndexKeys, skipRestore } (persistence slot, null while no user
  //   is known, plus the keys to keep when pruning and whether to start empty instead of
  //   restoring; sent once per worker (re)spawn)
  // - 'addDocs'/'updateDocs': RawSearchDoc[] (worker builds the SearchableDocument)
  // - 'removeDocs': string[] (doc ids)
  // - 'updateDoc': pre-built SearchableDocument (tag-rename fix-up path)
  // - 'search': SearchPayload
  // - 'replicationCompleted': boolean (persistIndex)
  // - 'rebuildStarted'/'rebuildFinished': no payload (rebuild-persist bracket)
  // - 'rebuildAborted': no payload (failed rebuild; clears removed-id tracking, persists stay suppressed)
  // - 'rebuildDocs': RawSearchDoc[] (rebuild snapshot chunk; skips ids removed mid-rebuild)
  // - 'reconcileStarted'/'reconcileFinished': no payload (reconcile bracket, archived index)
  // - 'reconcileDocs': RawSearchDoc[] (RxDB-truth snapshot chunk to diff)
  payload?:
    | SearchPayload
    | boolean
    | SearchableDocument
    | RawSearchDoc[]
    | string[]
    | { indexKey: string | null; validIndexKeys: string[]; skipRestore: boolean };
  highlightMatches?: boolean;
};

// The worker announces the outcome of restoring its persisted index once, when
// oramaDBReady settles. 'ready' also covers a fresh cold-start index (no blob).
export type WorkerIndexStatus = 'ready' | 'restore-failed';

// A debounced background persist failed in the worker with nothing awaiting it; the
// reason travels in `error`. Only the main thread can report it to Sentry.
export const PERSIST_FAILED_ACTION = 'persistFailed';
// The worker could not read or load its saved index at startup (the restore then fails into a
// rebuild). Like persistFailed, the main thread logs it because the worker's console never
// reaches Sentry.
export const RESTORE_FAILED_ACTION = 'restoreFailed';
// The worker's one-time answer to whether its restore found a stored index (see
// searchService.hasPersistedIndex); the startup index decision waits on it.
export const PERSISTED_INDEX_ACTION = 'persistedIndex';

export type WorkerResponse = {
  // Echoes the request action on responses; on an unsolicited broadcast it names the
  // broadcast instead ('indexStatus', 'persistedIndex', 'persistFailed', 'restoreFailed').
  action?: string;
  // Absent on unsolicited broadcasts (index status announcement, persist failure);
  // present on every response to a main-thread request.
  messageId?: string;
  success?: boolean;
  result?: SearchResponse;
  // The Error object itself: postMessage's structured clone preserves name, message and
  // stack, so a DOMException such as QuotaExceededError stays identifiable on this side.
  error?: Error;
  indexUpdated?: boolean;
  indexStatus?: WorkerIndexStatus;
  // The worker's one-time 'persistedIndex' announce: whether its restore found a stored index.
  persistedIndex?: boolean;
};
